import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import Layout  from '../components/Layout'
import { reportsApi, Report, Execution, ScheduleType, errorMsg } from '../api/client'
import { useIsMobile } from '../hooks/useIsMobile'

const C = { teal: '#2B4C5E', yellow: '#F2B705', muted: '#6B8A91', light: '#C7D9DB' }

const DAY_RANGES = [
  { value: 'MON-SAT', label: 'Mon–Sat' },
  { value: 'MON-FRI', label: 'Mon–Fri' },
  { value: 'MON-SUN', label: 'Every Day' },
]

const SCHEDULE_TYPES: { value: ScheduleType; label: string }[] = [
  { value: 'weekly',   label: 'Weekly' },
  { value: 'daily',    label: 'Daily' },
  { value: 'monthly',  label: 'Monthly' },
  { value: 'multiple', label: 'Multiple Times/Day' },
]

type EditForm = {
  name:                  string
  description:           string
  schedule_type:         ScheduleType
  schedule_times:        string[]
  schedule_days:         string
  schedule_day_of_month: number
  email_list_key:        string
  active:                boolean
}

export default function DashboardPage() {
  const { getToken, ready } = useAuth()
  const navigate     = useNavigate()
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState<string | null>(null)
  const [msg,     setMsg]     = useState('')
  const [isErr,   setIsErr]   = useState(false)

  // Edit modal state
  const [editing,  setEditing]  = useState<Report | null>(null)
  const [editForm, setEditForm] = useState<EditForm | null>(null)
  const [saving,   setSaving]   = useState(false)

  // Execution history per report
  const [execOpen, setExecOpen]   = useState<string | null>(null)
  const [execs,    setExecs]      = useState<Execution[]>([])
  const [execLoad, setExecLoad]   = useState(false)

  useEffect(() => { if (ready) loadReports() }, [ready])

  async function loadReports() {
    try {
      await getToken()
      const r = await reportsApi.list()
      setReports(r.data.reports ?? [])
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this report and its schedule?')) return
    await getToken()
    await reportsApi.delete(id)
    setReports(prev => prev.filter(r => r.report_id !== id))
    flash('Report deleted')
  }

  async function handleToggle(report: Report) {
    await getToken()
    await reportsApi.update(report.report_id, { active: !report.active })
    setReports(prev => prev.map(r =>
      r.report_id === report.report_id ? { ...r, active: !r.active } : r
    ))
  }

  async function handleRunNow(id: string) {
    setRunning(id)
    try {
      await getToken()
      await reportsApi.runNow(id)
      flash('Task triggered — check Logs for output')
    } catch (e: any) {
      flash(`Run failed: ${errorMsg(e, 'unknown')}`, true)
    } finally {
      setRunning(null)
    }
  }

  function openEdit(report: Report) {
    setEditing(report)
    setEditForm({
      name:                  report.name,
      description:           report.description,
      schedule_type:         report.schedule_type  || 'weekly',
      schedule_times:        report.schedule_times || [report.schedule_time || '10:00'],
      schedule_days:         report.schedule_days  || 'MON-SAT',
      schedule_day_of_month: report.schedule_day_of_month || 1,
      email_list_key:        report.email_list_key,
      active:                report.active,
    })
  }

  async function handleSaveEdit() {
    if (!editing || !editForm) return
    if (!editForm.schedule_times.length) { flash('At least one schedule time required', true); return }
    setSaving(true)
    try {
      await getToken()
      const updated = await reportsApi.update(editing.report_id, {
        ...editForm,
        schedule_time: editForm.schedule_times[0],
      })
      setReports(prev => prev.map(r =>
        r.report_id === editing.report_id ? updated.data : r
      ))
      setEditing(null)
      flash('Report updated')
    } catch (e: any) {
      flash(errorMsg(e, 'Update failed'), true)
    } finally {
      setSaving(false)
    }
  }

  async function toggleExecHistory(id: string) {
    if (execOpen === id) { setExecOpen(null); setExecs([]); return }
    setExecOpen(id)
    setExecs([])
    setExecLoad(true)
    try {
      await getToken()
      const r = await reportsApi.executions(id, 5)
      setExecs(r.data.executions ?? [])
    } catch { /* silent */ }
    finally { setExecLoad(false) }
  }

  function flash(text: string, err = false) {
    setIsErr(err); setMsg(text); setTimeout(() => setMsg(''), 5000)
  }

  const active   = reports.filter(r => r.active).length
  const inactive = reports.length - active

  return (
    <Layout>
      {/* Edit modal */}
      {editing && editForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={e => { if (e.target === e.currentTarget) setEditing(null) }}>
          <div style={{
            background: '#fff', borderRadius: 16, padding: 32,
            width: 580, maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <h2 style={{ color: C.teal, fontSize: 20, fontWeight: 700 }}>Edit Report</h2>
              <button onClick={() => setEditing(null)}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>
                ×
              </button>
            </div>

            {/* Name */}
            <Field label="Report Name">
              <input value={editForm.name}
                onChange={e => setEditForm(f => f && ({ ...f, name: e.target.value }))}
                style={inputStyle} />
            </Field>

            {/* Description */}
            <Field label="Description">
              <textarea value={editForm.description}
                onChange={e => setEditForm(f => f && ({ ...f, description: e.target.value }))}
                rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
            </Field>

            {/* Schedule type */}
            <Field label="Schedule Type">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {SCHEDULE_TYPES.map(st => (
                  <button key={st.value} type="button"
                    onClick={() => setEditForm(f => f && ({ ...f, schedule_type: st.value }))}
                    style={{
                      padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                      border: `1.5px solid ${editForm.schedule_type === st.value ? C.yellow : '#d1d5db'}`,
                      background: editForm.schedule_type === st.value ? 'rgba(242,183,5,0.08)' : '#fff',
                      color:  editForm.schedule_type === st.value ? C.teal : C.muted,
                      fontWeight: editForm.schedule_type === st.value ? 600 : 400,
                    }}>{st.label}</button>
                ))}
              </div>
            </Field>

            {/* Day range */}
            {(editForm.schedule_type === 'weekly' || editForm.schedule_type === 'multiple') && (
              <Field label="Run On">
                <div style={{ display: 'flex', gap: 8 }}>
                  {DAY_RANGES.map(d => (
                    <button key={d.value} type="button"
                      onClick={() => setEditForm(f => f && ({ ...f, schedule_days: d.value }))}
                      style={{
                        padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        border: `1.5px solid ${editForm.schedule_days === d.value ? C.yellow : '#d1d5db'}`,
                        background: editForm.schedule_days === d.value ? 'rgba(242,183,5,0.08)' : '#fff',
                        color: editForm.schedule_days === d.value ? C.teal : C.muted,
                        fontWeight: editForm.schedule_days === d.value ? 600 : 400,
                      }}>{d.label}</button>
                  ))}
                </div>
              </Field>
            )}

            {/* Day of month */}
            {editForm.schedule_type === 'monthly' && (
              <Field label="Day of Month (1–28)">
                <input type="number" min={1} max={28}
                  value={editForm.schedule_day_of_month}
                  onChange={e => setEditForm(f => f && ({ ...f, schedule_day_of_month: parseInt(e.target.value) || 1 }))}
                  style={{ ...inputStyle, width: 100 }} />
              </Field>
            )}

            {/* Times */}
            <Field label={editForm.schedule_type === 'multiple' ? 'Run Times (IST)' : 'Run Time (IST)'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {editForm.schedule_times.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="time" value={t}
                      onChange={e => {
                        const times = [...editForm.schedule_times]; times[i] = e.target.value
                        setEditForm(f => f && ({ ...f, schedule_times: times }))
                      }}
                      style={{ ...inputStyle, width: 160 }} />
                    {editForm.schedule_type === 'multiple' && editForm.schedule_times.length > 1 && (
                      <button type="button"
                        onClick={() => setEditForm(f => f && ({ ...f, schedule_times: f.schedule_times.filter((_, idx) => idx !== i) }))}
                        style={{ color: '#dc2626', background: '#fff1f2', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
                        −
                      </button>
                    )}
                  </div>
                ))}
                {editForm.schedule_type === 'multiple' && (
                  <button type="button"
                    onClick={() => setEditForm(f => f && ({ ...f, schedule_times: [...f.schedule_times, '10:00'] }))}
                    style={{ alignSelf: 'flex-start', padding: '6px 14px', borderRadius: 6, background: '#f0fdf4', color: '#16a34a', border: '1px solid #dcfce7', fontSize: 12, cursor: 'pointer' }}>
                    + Add Time
                  </button>
                )}
              </div>
            </Field>

            {/* Email list */}
            <Field label="Email List Key">
              <input value={editForm.email_list_key}
                onChange={e => setEditForm(f => f && ({ ...f, email_list_key: e.target.value }))}
                style={inputStyle} />
            </Field>

            {/* Active */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 24px' }}>
              <input type="checkbox" checked={editForm.active}
                onChange={e => setEditForm(f => f && ({ ...f, active: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: C.yellow }} />
              <span style={{ color: C.teal, fontSize: 13, fontWeight: 600 }}>Active (schedule running)</span>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSaveEdit} disabled={saving} style={{
                background: C.yellow, color: C.teal, border: 'none',
                padding: '10px 28px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={() => setEditing(null)} style={{
                background: '#f3f4f6', color: C.muted, border: 'none',
                padding: '10px 20px', borderRadius: 8, fontSize: 14, cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <main style={{ padding: '24px 16px', minHeight: 'calc(100vh - 64px)', overflowX: 'hidden' }}>

        {msg && (
          <div style={{
            position: 'fixed', top: 80, right: 24,
            background: isErr ? '#7f1d1d' : C.teal,
            color: '#fff', padding: '12px 20px', borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 2000,
            borderLeft: `4px solid ${isErr ? '#ef4444' : C.yellow}`, maxWidth: 420,
          }}>{msg}</div>
        )}

        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: C.teal, fontSize: 24, fontWeight: 700 }}>Dashboard</h1>
          <p style={{ color: C.muted, fontSize: 14, marginTop: 4 }}>
            Scheduled reports — automated email delivery
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total Reports', value: reports.length, color: C.teal },
            { label: 'Active',        value: active,          color: '#16a34a' },
            { label: 'Paused',        value: inactive,        color: C.muted },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 12, padding: '20px 24px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              borderTop: `3px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 32, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ color: C.teal, fontSize: 18, fontWeight: 600 }}>Scheduled Reports</h2>
          <button onClick={() => navigate('/upload')} style={btnStyle(C.yellow, C.teal)}>
            + Add Report
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Loading reports…</div>
        ) : reports.length === 0 ? (
          <Empty onAdd={() => navigate('/upload')} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {reports.map(r => (
              <div key={r.report_id}>
                <ReportCard
                  report={r}
                  running={running === r.report_id}
                  execOpen={execOpen === r.report_id}
                  onDelete={() => handleDelete(r.report_id)}
                  onToggle={() => handleToggle(r)}
                  onRun={() => handleRunNow(r.report_id)}
                  onEdit={() => openEdit(r)}
                  onHistory={() => toggleExecHistory(r.report_id)}
                />
                {execOpen === r.report_id && (
                  <ExecPanel execs={execs} loading={execLoad} />
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </Layout>
  )
}

function schedLabel(r: Report): string {
  const type  = r.schedule_type  || 'weekly'
  const times = (r.schedule_times || [r.schedule_time || '10:00']).join(' & ')
  const days  = { 'MON-SAT': 'Mon–Sat', 'MON-FRI': 'Mon–Fri', 'MON-SUN': 'Daily' }[r.schedule_days || 'MON-SAT'] || r.schedule_days || 'Mon–Sat'
  if (type === 'daily')   return `${times} IST · Daily`
  if (type === 'monthly') return `${times} IST · Day ${r.schedule_day_of_month} monthly`
  return `${times} IST · ${days}`
}

function ReportCard({ report, running, execOpen, onDelete, onToggle, onRun, onEdit, onHistory }: {
  report:    Report
  running:   boolean
  execOpen:  boolean
  onDelete:  () => void
  onToggle:  () => void
  onRun:     () => void
  onEdit:    () => void
  onHistory: () => void
}) {
  const scriptName = report.script_key.split('/').pop()
  const isMobile = useIsMobile()

  return (
    <div style={{
      background: '#fff', borderRadius: 12,
      boxShadow:  '0 1px 4px rgba(0,0,0,0.08)',
      padding:    isMobile ? '14px 16px' : '18px 24px',
      display:    'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems:  isMobile ? 'flex-start' : 'center',
      gap:         isMobile ? 10 : 20,
      borderLeft: `4px solid ${report.active ? C.yellow : '#e5e7eb'}`,
      opacity:    report.active ? 1 : 0.75,
      overflow:   'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minWidth: 0 }}>
        <div style={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: report.active ? '#16a34a' : '#9ca3af',
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{report.name}</div>
          {report.description && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{report.description}</div>
          )}
          <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <Tag icon="🐍" label={scriptName || report.script_key} />
            <Tag icon="🕐" label={schedLabel(report)} />
            <Tag icon="📧" label={report.email_list_key.split('/').pop() || ''} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
        <button onClick={onRun} disabled={running} style={actionBtn('#f0f9ff', C.teal)}>
          {running ? '⏳' : '▶ Run'}
        </button>
        <button onClick={onToggle}
          style={actionBtn(report.active ? '#fef3c7' : '#f0fdf4', report.active ? '#b45309' : '#16a34a')}>
          {report.active ? 'Pause' : 'Resume'}
        </button>
        <button onClick={onEdit} style={actionBtn('#f0f4ff', '#4f46e5')}>Edit</button>
        <button onClick={onHistory}
          style={actionBtn(execOpen ? '#fffbeb' : '#f9fafb', execOpen ? '#b45309' : C.muted)}>
          History
        </button>
        <button onClick={onDelete} style={actionBtn('#fff1f2', '#dc2626')}>Delete</button>
      </div>
    </div>
  )
}

function ExecPanel({ execs, loading }: { execs: Execution[]; loading: boolean }) {
  return (
    <div style={{
      background: '#1a3348', borderRadius: '0 0 10px 10px',
      padding: '12px 28px', marginTop: -4,
    }}>
      {loading ? (
        <div style={{ color: C.light, fontSize: 12, padding: '4px 0' }}>Loading history…</div>
      ) : execs.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 12 }}>No executions recorded yet.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: C.muted, textAlign: 'left' }}>
              <th style={{ padding: '4px 8px 8px 0' }}>Started</th>
              <th style={{ padding: '4px 8px 8px' }}>Status</th>
              <th style={{ padding: '4px 8px 8px' }}>Duration</th>
              <th style={{ padding: '4px 8px 8px' }}>Triggered by</th>
            </tr>
          </thead>
          <tbody>
            {execs.map(e => (
              <tr key={e.started_at} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 8px 6px 0', color: C.light }}>
                  {new Date(e.started_at).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short',
                    hour: '2-digit', minute: '2-digit', hour12: false,
                    timeZone: 'Asia/Kolkata',
                  })}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: e.status === 'SUCCESS' ? '#14532d' : e.status === 'FAILED' ? '#7f1d1d' : '#713f12',
                    color: e.status === 'SUCCESS' ? '#86efac' : e.status === 'FAILED' ? '#fca5a5' : '#fde68a',
                  }}>{e.status}</span>
                </td>
                <td style={{ padding: '6px 8px', color: C.muted }}>
                  {e.duration_s != null ? `${Math.floor(e.duration_s / 60)}m ${e.duration_s % 60}s` : '—'}
                </td>
                <td style={{ padding: '6px 8px', color: C.muted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.triggered_by}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function Tag({ icon, label }: { icon: string; label: string }) {
  return (
    <span style={{ fontSize: 11, color: C.muted, display: 'flex', alignItems: 'center', gap: 4 }}>
      {icon} {label}
    </span>
  )
}

function Empty({ onAdd }: { onAdd: () => void }) {
  return (
    <div style={{
      textAlign: 'center', padding: '60px 40px',
      background: '#fff', borderRadius: 12,
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.teal, marginBottom: 8 }}>No reports yet</div>
      <div style={{ color: C.muted, fontSize: 14, marginBottom: 24 }}>
        Upload a Python script and schedule it for automated delivery
      </div>
      <button onClick={onAdd} style={btnStyle(C.yellow, C.teal)}>+ Add First Report</button>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  )
}

function btnStyle(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, border: 'none', padding: '10px 20px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 }
}

function actionBtn(bg: string, color: string): React.CSSProperties {
  return { background: bg, color, border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px',
  border: '1px solid #d1d5db', borderRadius: 6,
  fontSize: 13, color: C.teal, outline: 'none', boxSizing: 'border-box',
}
